import Chip from "@mui/material/Chip";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ItemCategorySuggestion } from "@egalregal/shared";
import { useT, type MessageKey, type Translate } from "../../shared/i18n";

type CategoryChipColor = "primary" | "success" | "info" | "secondary" | "default";

/** One color per suggestion; custom values fall back to the default. */
const CATEGORY_COLORS = {
  book: "primary",
  food: "success",
  device: "info",
  media: "secondary",
  other: "default",
} satisfies Record<ItemCategorySuggestion, CategoryChipColor>;

const categoryColor = new Map<string, CategoryChipColor>(Object.entries(CATEGORY_COLORS));

/** Message keys for the closed suggestion set; custom values have none. */
const categoryI18nKey = new Map<string, MessageKey>(
  Object.entries({
    book: "category_book",
    food: "category_food",
    device: "category_device",
    media: "category_media",
    other: "category_other",
  } satisfies Record<ItemCategorySuggestion, MessageKey>),
);

/**
 * The display label of an item category: known suggestions translate, custom
 * values print as stored. There is deliberately no fallback to `Other` — a
 * custom value must never be mislabelled as a different category.
 */
export function itemCategoryLabel(category: string, t: Translate): string {
  const key = categoryI18nKey.get(category);
  return key ? t(key) : category;
}

/** Badge chip showing a localized item category; custom values render raw. */
export function CategoryChip({ category, sx }: { category: string; sx?: SxProps<Theme> }) {
  const t = useT();
  return (
    <Chip
      label={itemCategoryLabel(category, t)}
      size="small"
      color={categoryColor.get(category) ?? "default"}
      sx={sx}
    />
  );
}
